import classNames from "classnames";
import React, { FC, FormEvent, useState } from "react";

import { useSetPortalPasswordMutation } from "../../../slices/PortalSlice";
import { errorMessage, passwordProblem } from "../portal.utils";
import {
  PortalAlert,
  portalInput,
  portalLabel,
  portalPrimary,
} from "./PortalUi";

/** Set or change the portal password (signing in with codes keeps working). */
export const PortalSetPassword: FC<{
  hasPassword: boolean;
  onClose: () => void;
}> = ({ hasPassword, onClose }) => {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [save, saveState] = useSetPortalPasswordMutation();

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    const problem = passwordProblem(password);
    if (problem) return setError(problem);
    if (password !== confirm) return setError("The two passwords don't match.");
    const result = await save({ password });
    if ("error" in result) return setError(errorMessage(result.error));
    setDone(true);
  };

  return (
    <div
      className={
        "fixed inset-0 z-50 flex items-center justify-center bg-[rgba(31,36,51,0.45)] px-4"
      }
      role={"dialog"}
      aria-modal={"true"}
      aria-labelledby={"portal-pw-title"}>
      <form
        onSubmit={submit}
        noValidate={true}
        className={
          "flex w-full max-w-[420px] flex-col gap-4 rounded-2xl bg-white p-6 shadow-[0_20px_60px_rgba(31,36,51,0.25)]"
        }>
        <h2 id={"portal-pw-title"} className={"m-0 text-xl font-extrabold"}>
          {hasPassword ? "Change password" : "Set a password"}
        </h2>
        {done ? (
          <>
            <PortalAlert tone={"success"}>
              Saved. Next time you can sign in with your email and this
              password, or with a code.
            </PortalAlert>
            <button type={"button"} className={portalPrimary} onClick={onClose}>
              Done
            </button>
          </>
        ) : (
          <>
            <div className={"flex flex-col gap-1.5"}>
              <label htmlFor={"portal-pw"} className={portalLabel}>
                New password
              </label>
              <input
                id={"portal-pw"}
                type={"password"}
                autoComplete={"new-password"}
                className={classNames(portalInput, "font-medium")}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoFocus={true}
              />
              <span className={"text-xs font-medium text-[#6B7185]"}>
                At least 8 characters, with letters and a number or symbol.
              </span>
            </div>
            <div className={"flex flex-col gap-1.5"}>
              <label htmlFor={"portal-pw2"} className={portalLabel}>
                Type it again
              </label>
              <input
                id={"portal-pw2"}
                type={"password"}
                autoComplete={"new-password"}
                className={classNames(portalInput, "font-medium")}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
            </div>
            {error && <PortalAlert>{error}</PortalAlert>}
            <div className={"flex gap-2.5"}>
              <button
                type={"button"}
                onClick={onClose}
                className={
                  "h-[46px] flex-1 rounded-[9px] border-[1.5px] border-[#DCE2EA] text-sm font-bold text-[#3A4256]"
                }>
                Cancel
              </button>
              <button
                type={"submit"}
                className={classNames(portalPrimary, "flex-1")}
                disabled={saveState.isLoading}>
                {saveState.isLoading ? "Saving…" : "Save"}
              </button>
            </div>
          </>
        )}
      </form>
    </div>
  );
};
